import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { IoCheckmark, IoChevronDown } from 'react-icons/io5';
import { Fonts, Palette } from '../../components/utils';

const MENU_MAX_HEIGHT = 384; // ~8 rows

/**
 * Shared single-select radio dropdown (Figma-style bordered panel).
 * Used for building + resident pickers in chat.
 */
const ChatRadioSelect = ({
  value,
  onChange,
  options = [],
  getOptionId = (option) => option?.id,
  getOptionLabel = (option) => option?.name || '',
  placeholder = '',
  ariaLabel,
  disabled = false,
  loading = false,
  loadingContent = null,
  emptyContent = null,
  errorContent = null,
  footer = null,
  selectedLabel: selectedLabelOverride,
  maxMenuHeight = MENU_MAX_HEIGHT,
  variant = 'outlined'
}) => {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const selectedId = Number(value) || 0;
  const filled = variant === 'filled';

  const selectedLabel = useMemo(() => {
    if (selectedLabelOverride != null && selectedLabelOverride !== '') {
      return selectedLabelOverride;
    }
    const match = (options || []).find(
      (option) => Number(getOptionId(option)) === selectedId
    );
    return match ? getOptionLabel(match) : '';
  }, [options, selectedId, getOptionId, getOptionLabel, selectedLabelOverride]);

  const triggerLabel = selectedLabel || placeholder || '';
  const isPlaceholder = !selectedLabel;

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const handleSelect = (option) => {
    const id = Number(getOptionId(option)) || 0;
    setOpen(false);
    if (typeof onChange === 'function') {
      onChange(id, option);
    }
  };

  const showOptions = !loading && !errorContent && (options || []).length > 0;

  return (
    <Panel ref={wrapRef} $disabled={disabled} $open={open} $filled={filled}>
      <Trigger
        type="button"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel || triggerLabel || placeholder}
        $open={open}
        $filled={filled}
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
      >
        <TriggerText $placeholder={isPlaceholder}>{triggerLabel}</TriggerText>
        <Chevron aria-hidden="true">
          <IoChevronDown size={16} />
        </Chevron>
      </Trigger>

      {open && (
        <Menu
          role="listbox"
          aria-label={ariaLabel || placeholder || 'Options'}
          $maxHeight={maxMenuHeight}
        >
          {loading && <MenuStatus>{loadingContent}</MenuStatus>}
          {!loading && errorContent && <MenuStatus>{errorContent}</MenuStatus>}
          {!loading && !errorContent && (options || []).length === 0 && (
            <MenuStatus>{emptyContent}</MenuStatus>
          )}
          {showOptions &&
            (options || []).map((option) => {
              const id = Number(getOptionId(option)) || 0;
              const selected = id === selectedId;
              return (
                <Option
                  key={id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => handleSelect(option)}
                >
                  <OptionName>{getOptionLabel(option)}</OptionName>
                  <RadioMark $selected={selected} aria-hidden="true">
                    {selected && <IoCheckmark size={12} />}
                  </RadioMark>
                </Option>
              );
            })}
          {showOptions && footer}
        </Menu>
      )}
    </Panel>
  );
};

const Panel = styled.div`
  box-sizing: border-box;
  position: relative;
  width: 100%;
  flex-shrink: 0;
  z-index: ${({ $open }) => ($open ? 30 : 1)};
  border: ${({ $filled }) => ($filled ? 'none' : '1px solid #d5d5d5')};
  border-radius: 8px;
  background: ${({ $filled }) => ($filled ? Palette.WHITE_SMOKE : Palette.WHITE)};
  overflow: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
`;

const Trigger = styled.button`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: ${({ $filled }) => ($filled ? '56px' : '48px')};
  height: ${({ $filled }) => ($filled ? '56px' : '48px')};
  padding: 0 16px;
  border: none;
  border-radius: ${({ $open }) => ($open ? '8px 8px 0 0' : '8px')};
  border-bottom: ${({ $open }) => ($open ? `1px solid ${Palette.SOLITUDE}` : 'none')};
  background: ${({ $filled }) => ($filled ? Palette.WHITE_SMOKE : Palette.WHITE)};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  cursor: pointer;
  text-align: left;

  &:disabled {
    cursor: default;
  }

  &:focus {
    outline: none;
  }
`;

const TriggerText = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $placeholder }) => ($placeholder ? Palette.CONTENT_TERTIARY : Palette.BLACK)};
`;

const Chevron = styled.span`
  color: ${Palette.BLACK};
  display: inline-flex;
  flex-shrink: 0;
`;

const Menu = styled.div`
  box-sizing: border-box;
  position: absolute;
  left: -1px;
  right: -1px;
  top: calc(100% - 1px);
  z-index: 31;
  max-height: ${({ $maxHeight }) => `${$maxHeight}px`};
  overflow-y: auto;
  border: 1px solid #d5d5d5;
  border-radius: 0 0 8px 8px;
  background: ${Palette.WHITE};
  box-shadow: 0 10px 20px rgba(16, 24, 40, 0.08);
`;

const MenuStatus = styled.div`
  width: 100%;
`;

export const Option = styled.button`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 48px;
  padding: 12px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${Palette.GREY_50};
  }

  &:focus {
    outline: none;
  }
`;

export const OptionName = styled.span`
  flex: 1;
  min-width: 0;
  color: ${Palette.BLACK};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RadioMark = styled.span`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1.5px solid ${Palette.HAZY_BLUE};
  background: ${({ $selected }) => ($selected ? Palette.HAZY_BLUE : Palette.WHITE)};
  color: ${Palette.WHITE};
`;

export const MenuEmpty = styled.p`
  margin: 0;
  padding: 16px;
  color: ${Palette.CONTENT_TERTIARY};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
`;

export const LoadMoreButton = styled.button`
  display: block;
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-top: 1px solid ${Palette.SOLITUDE};
  background: ${Palette.WHITE};
  color: ${Palette.HAZY_BLUE};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
  text-align: center;

  &:disabled {
    color: ${Palette.CONTENT_TERTIARY};
    cursor: default;
  }

  &:focus {
    outline: none;
  }
`;

export default ChatRadioSelect;
